import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useWeb3Context } from '../../contexts/web3Context'
import { alert } from '../Alert/Alert'
import { getSharedPvPSocket } from '../../socket/pvpSharedSocket'
import {
  clearDirectChallengeFlow,
  setCurrentRoom,
  setDirectChallengeActiveRoomId,
  setDirectChallengeBattleStartPending,
  setDirectChallengeDeclinedMessage,
  setDirectChallengeJoinerLock,
  setDirectChallengeRespondBusy,
  setDirectChallengeAwaitingJoin,
  setIncomingDirectChallenge,
} from '../../common/state/pvp/reducer'
import type { PvpIncomingDirectChallengeRequest } from '../../common/state/pvp/state'
import store from '../../store'
import type { RootState } from '../../store'
import {
  getPvPRoomDetails,
  postPvpDirectChallengeAccept,
  postPvpDirectChallengeCancel,
  postPvpDirectChallengeDecline,
  postPvpProcessPayment,
} from '../../common/api'
import type { PvPRoom } from '../../common/api'
import {
  pvpLobbyTitleDividerLeftUrl,
  pvpLobbyTitleDividerRightUrl,
  pvpLoserIconUrl,
  pvpWinnerIconUrl,
} from '../PvP/PvPBackground'
import { localPublicAssetUrl, pvpDirectChallengeAssetUrl, pvpHubAssetUrl } from '../PvP/utils/pvpAssetUrls'
import { PartyFormatIcons } from '../PvP/DirectChallengeCharacterCountPanel'
import { useServerNowMs } from '../../hooks/useServerNowMs'
import { formatDirectChallengeJoinPrepMmSs } from '../../hooks/useDirectChallengeJoinPrepRemainingMs'
import {
  DIRECT_CHALLENGE_COUNTDOWN_EVENT,
  type DirectChallengeCountdownDetail,
} from '../../utils/pvp/directChallengeFlowEvents'
import { DirectChallengeAwaitingHourglassLottie } from './DirectChallengeAwaitingHourglassLottie'

const PVP_LOBBY_PAGE_TITLE_DIVIDER_LEFT_URL = pvpLobbyTitleDividerLeftUrl
const PVP_LOBBY_PAGE_TITLE_DIVIDER_RIGHT_URL = pvpLobbyTitleDividerRightUrl
const RANKED_MARK_URL = pvpHubAssetUrl('ranked-mark.webp')
const UNRANKED_MARK_URL = pvpHubAssetUrl('unranked-mark.webp')
/** Same URLs as `DirectChallengeBattleSettingsPanel` stake currency toggles. */
const INCOMING_STAKE_USDT_ICON_URL =
  'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/mining-factory/usdt-icon.webp'
const INCOMING_STAKE_ECS_ICON_URL =
  'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/eclipsera-gem.webp'
/** Incoming modal free-stake payout bullet icons — Blob `pvp/direct-challenge/`. */
const INCOMING_UNRANKED_PAYOUT_ICON_URLS = [
  pvpDirectChallengeAssetUrl('noMMRChange.webp'),
  pvpDirectChallengeAssetUrl('noStakePayout.webp'),
  pvpDirectChallengeAssetUrl('playForFunAndPractice.webp'),
] as const

const INCOMING_FREE_PAYOUT_BULLETS = [
  {
    key: 'pvp.directChallengeIncomingUnrankedPayoutBulletNoStake' as const,
    iconIndex: 1,
  },
  {
    key: 'pvp.directChallengeIncomingUnrankedPayoutBulletFun' as const,
    iconIndex: 2,
  },
] as const

/** Ranked stake payout multipliers (aligned with DirectChallengeBattleSettingsPanel). */
const INCOMING_PAYOUT_WINNER_MULT = '1.9x'
const INCOMING_PAYOUT_LOSER_MULT = '0'
const INCOMING_PAYOUT_PLATFORM_MULT = '0.1x'

/** Same as stake summary aside in `DirectChallengeBattleSettingsPanel`. */
const INCOMING_STAKE_PAYOUT_PLATFORM_ICON_URL = localPublicAssetUrl('/icons/fav.png')
const INCOMING_PAYOUT_ASIDE_PAD_W_FR = 0.1
const INCOMING_PAYOUT_ASIDE_PAD_H_FR = 0.03
const INCOMING_PAYOUT_ASIDE_GAP_FR = 0.1
const INCOMING_PAYOUT_ASIDE_FONT_FR = 0.07
const INCOMING_PAYOUT_ASIDE_ICON_FR = 0.15
/** Left-column titles (Winner / Loser / Platform) vs Tilt Warp values — titles slightly smaller. */
const INCOMING_PAYOUT_TITLE_VS_VALUE_FONT = 0.88

/** Section title row in battle/payout cards: 10% of modal height (7% + 3%); text uses 60cqh inside this band. */
function IncomingModalSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2
      className="shrink-0 box-border flex min-h-0 min-w-0 items-center truncate border-b border-white/10 font-semibold tracking-wide text-amber-100/95"
      style={{
        fontFamily: "'Cinzel', 'Times New Roman', serif",
        boxSizing: 'border-box',
        height: '10cqh',
        minHeight: '10cqh',
        maxHeight: '10cqh',
        paddingLeft: '2cqi',
        paddingRight: '2cqi',
        lineHeight: 1,
        containerType: 'size',
      }}
    >
      <span className="block min-w-0 truncate leading-none" style={{ fontSize: '60cqh' }}>
        {children}
      </span>
    </h2>
  )
}

/** Same gold gradient title treatment as `pvp-direct-challenge-page` header. */
const DIRECT_CHALLENGE_MODAL_TITLE_STYLE: React.CSSProperties = {
  fontFamily: "'Cinzel', 'Times New Roman', serif",
  fontWeight: 800,
  backgroundImage:
    'linear-gradient(180deg, #2a1f08 0%, #5c430c 14%, #8a6a12 28%, #c9a227 42%, #f0d060 52%, #fff8dc 58%, #e6bc2e 68%, #a67c1a 82%, #5c4510 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  filter:
    'drop-shadow(0 2px 2px rgba(0,0,0,0.95)) drop-shadow(0 0 16px rgba(212,167,40,0.38))',
}

function CloseCanvasIcon({
  className,
  style,
}: {
  className?: string
  style?: React.CSSProperties
}) {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null)

  React.useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const size = 96
    canvas.width = size
    canvas.height = size
    ctx.clearRect(0, 0, size, size)
    ctx.strokeStyle = '#fff7ed'
    ctx.lineWidth = 11
    ctx.lineCap = 'round'
    ctx.shadowColor = 'rgba(0, 0, 0, 0.85)'
    ctx.shadowBlur = 7
    ctx.shadowOffsetY = 2
    ctx.beginPath()
    ctx.moveTo(28, 28)
    ctx.lineTo(68, 68)
    ctx.moveTo(68, 28)
    ctx.lineTo(28, 68)
    ctx.stroke()
  }, [])

  return <canvas ref={canvasRef} aria-hidden className={className} style={style} />
}

/** Declined modal — opponent nickname: light gold top → slightly deeper gold bottom. */
const DC_DECLINED_HORIZONTAL_NICKNAME_STYLE: React.CSSProperties = {
  fontFamily: "'Cinzel', 'Times New Roman', serif",
  fontWeight: 700,
  backgroundImage:
    'linear-gradient(180deg, #fffdf5 0%, #fff8e8 18%, #fcefb8 40%, #e8cf72 68%, #b8892a 92%, #8a6824 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  WebkitTextFillColor: 'transparent',
  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.9)) drop-shadow(0 0 10px rgba(212,167,40,0.35))',
}

function requesterAvatarInitial(nickname: string, wallet: string): string {
  const n = nickname.trim()
  if (n) {
    const cp = n.codePointAt(0)
    const ch = cp !== undefined ? String.fromCodePoint(cp) : ''
    if (ch && /^[a-z]$/i.test(ch)) return ch.toUpperCase()
    if (ch) return ch
  }
  const w = wallet.replace(/^0x/i, '').trim()
  if (w) {
    const c = w[0]
    return c ? c.toUpperCase() : '?'
  }
  return '?'
}

function userAvatarImageUrl(
  user: {
    currentAvatar?: string | null | { id?: string | null; imageUrl?: string | null }
  } | null,
): string | null {
  if (!user?.currentAvatar) return null
  if (typeof user.currentAvatar === 'string') {
    const s = user.currentAvatar.trim()
    return s || null
  }
  const u = user.currentAvatar.imageUrl?.trim()
  return u || null
}

const DIRECT_CHALLENGE_WAITING_SUMMARY_SANS =
  '"Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, "Roboto", sans-serif'
export function DirectChallengeAwaitingJoinSummaryStrip({
  columns,
}: {
  columns: readonly [
    { title: string; value: string },
    { title: string; value: string },
    { title: string; value: string },
  ]
}) {
  return (
    <div
      className="flex min-h-0 w-full min-w-0 flex-1 flex-col justify-stretch"
      style={{
        boxSizing: 'border-box',
        minHeight: 0,
        containerType: 'size',
        paddingLeft: '10cqi',
        paddingRight: '10cqi',
        paddingTop: '3cqh',
        paddingBottom: '3cqh',
      }}
    >
      <div
        className="grid min-h-0 min-w-0 flex-1 overflow-hidden rounded-lg border border-amber-400/40 bg-gradient-to-b from-amber-950/45 via-zinc-950/80 to-black/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.07)]"
        style={{
          gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)',
          alignItems: 'stretch',
          boxSizing: 'border-box',
          minHeight: 0,
        }}
      >
        {columns.map((col, i) => (
          <div
            key={col.title}
            className="flex min-h-0 min-w-0 flex-col"
            style={{
              boxSizing: 'border-box',
              containerType: 'size',
              paddingLeft: '5cqi',
              paddingRight: '5cqi',
              paddingTop: '2cqh',
              paddingBottom: '2cqh',
              minHeight: 0,
              display: 'grid',
              gridTemplateRows: '35fr 5fr 60fr',
              ...(i < columns.length - 1
                ? { borderRight: '1px solid rgba(251, 191, 36, 0.22)' }
                : {}),
            }}
          >
            <div
              className="flex min-h-0 items-center justify-center text-center"
              style={{ containerType: 'size', minHeight: 0 }}
            >
              <span
                className="block max-w-full truncate"
                style={{
                  fontFamily: DIRECT_CHALLENGE_WAITING_SUMMARY_SANS,
                  fontSize: '60cqh',
                  lineHeight: 1.1,
                  fontWeight: 600,
                  color: 'rgba(254, 243, 199, 0.95)',
                }}
              >
                {col.title}
              </span>
            </div>
            <div aria-hidden className="min-h-0" style={{ minHeight: 0 }} />
            <div
              className="flex min-h-0 items-center justify-center text-center"
              style={{ containerType: 'size', minHeight: 0 }}
            >
              <span
                className="block max-w-full break-words [overflow-wrap:anywhere]"
                style={{
                  fontFamily: "'Tilt Warp', sans-serif",
                  fontSize: '60cqh',
                  lineHeight: 1.05,
                  color: '#ffffff',
                }}
              >
                {col.value}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Left/right columns under waiting modal title: row heights 5+40+5+20+5+20+5 (%). */
function AwaitingJoinPlayerStripColumn({
  nickname,
  wallet,
  avatarUrl,
  avatarFailed,
  onAvatarError,
  level,
  mmr,
  layout = 'vertical',
}: {
  nickname: string
  wallet: string
  avatarUrl: string | null
  avatarFailed: boolean
  onAvatarError: () => void
  level: number
  mmr: number
  /** `horizontal`: avatar left, name/level/MMR right (e.g. declined modal). */
  layout?: 'vertical' | 'horizontal'
}) {
  const { t } = useTranslation()
  const showPhoto = Boolean(avatarUrl) && !avatarFailed

  const avatarFrame = (
    <div className="box-border aspect-square h-full max-h-full max-w-full overflow-hidden rounded-full border-2 border-amber-400/75 bg-zinc-900/90">
      {showPhoto ? (
        <img
          src={avatarUrl!}
          alt=""
          className="h-full w-full object-cover"
          draggable={false}
          onError={onAvatarError}
        />
      ) : (
        <div
          className="flex h-full w-full select-none items-center justify-center bg-zinc-800/90 font-semibold text-amber-100/95"
          style={{ fontSize: 'min(18cqh, 1.75rem)' }}
          aria-hidden
        >
          {requesterAvatarInitial(nickname, wallet)}
        </div>
      )}
    </div>
  )

  if (layout === 'horizontal') {
    return (
      <div
        className="flex min-h-0 min-w-0 w-full flex-row items-stretch"
        style={{
          height: '100%',
          width: '100%',
          containerType: 'size',
          boxSizing: 'border-box',
        }}
      >
        {/* Left 50%: avatar hugging the vertical midline (right-aligned in this half). */}
        <div className="flex h-full min-h-0 w-1/2 min-w-0 flex-row items-center justify-end pr-[2cqi]">
          <div className="aspect-square h-[min(100%,68cqh)] max-h-full w-auto max-w-full shrink-0">
            {avatarFrame}
          </div>
        </div>
        {/* Right 50%: opponent info left-aligned from the center. */}
        <div
          className="flex h-full min-h-0 w-1/2 min-w-0 flex-col items-start justify-center pl-[2cqi]"
          style={{ rowGap: '0.35em' }}
        >
          <span
            className="w-full min-w-0 truncate text-left leading-tight"
            style={{
              ...DC_DECLINED_HORIZONTAL_NICKNAME_STYLE,
              fontSize: 'min(26cqh, 1.55rem)',
              lineHeight: 1.15,
            }}
          >
            {nickname.trim() || wallet.slice(0, 8) || t('pvp.player')}
          </span>
          <span
            className="w-full text-left text-white/85"
            style={{
              fontFamily: "'Tilt Warp', sans-serif",
              fontSize: 'min(15cqh, 0.98rem)',
            }}
          >
            {t('pvp.directChallengeIncomingLevelLabel', { level })}
          </span>
          <div className="flex w-full min-w-0 flex-row flex-wrap items-baseline justify-start gap-x-1 gap-y-0">
            <span className="shrink-0 text-white/65" style={{ fontSize: 'min(13cqh, 0.88rem)' }}>
              {t('pvp.directChallengeIncomingMmRTitle')}
            </span>
            <span
              className="font-semibold tabular-nums text-amber-100/95"
              style={{ fontSize: 'min(13cqh, 0.88rem)' }}
            >
              {mmr.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="min-h-0 min-w-0"
      style={{
        display: 'grid',
        gridTemplateRows: '5% 40% 5% 20% 5% 20% 5%',
        height: '100%',
        width: '100%',
        containerType: 'size',
      }}
    >
      <div aria-hidden className="min-h-0 min-w-0" />
      <div className="flex min-h-0 min-w-0 w-full items-center justify-center">
        <div className="aspect-square h-full max-h-full w-auto max-w-[88%] shrink-0">{avatarFrame}</div>
      </div>
      <div aria-hidden className="min-h-0 min-w-0" />
      <div className="flex min-h-0 min-w-0 items-center justify-center px-0.5">
        <span
          className="truncate text-center font-semibold text-white/95"
          style={{
            fontFamily: '"Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, "Roboto", sans-serif',
            fontSize: 'min(12cqh, 0.95rem)',
          }}
        >
          {nickname.trim() || wallet.slice(0, 8) || t('pvp.player')}
        </span>
      </div>
      <div aria-hidden className="min-h-0 min-w-0" />
      <div className="flex min-h-0 min-w-0 flex-col items-center justify-center gap-0.5 px-0.5 text-center">
        <span
          className="text-white/85"
          style={{
            fontFamily: "'Tilt Warp', sans-serif",
            fontSize: 'min(10cqh, 0.78rem)',
          }}
        >
          {t('pvp.directChallengeIncomingLevelLabel', { level })}
        </span>
        <div className="flex flex-row flex-wrap items-center justify-center gap-x-1 gap-y-0">
          <span className="text-white/65" style={{ fontSize: 'min(9cqh, 0.72rem)' }}>
            {t('pvp.directChallengeIncomingMmRTitle')}
          </span>
          <span
            className="font-semibold tabular-nums text-amber-100/95"
            style={{ fontSize: 'min(9cqh, 0.72rem)' }}
          >
            {mmr.toLocaleString()}
          </span>
        </div>
      </div>
      <div aria-hidden className="min-h-0 min-w-0" />
    </div>
  )
}

function IncomingBattleSettingRow({
  label,
  value,
  leading,
}: {
  label: React.ReactNode
  value: React.ReactNode
  /** Optional icon area (15% row width): mode marks, party-size silhouettes, etc. */
  leading?: React.ReactNode
}) {
  const fontStyle: React.CSSProperties = { fontSize: 'clamp(0.55rem, 30cqh, 1.2rem)' }
  return (
    <div
      className="box-border flex h-full min-h-0 w-full min-w-0 flex-row items-center gap-[5%] px-[2cqi]"
      style={{ containerType: 'size' }}
    >
      <div className="flex h-full min-h-0 w-[15%] shrink-0 items-center justify-center overflow-hidden">
        {leading != null ? (
          <div
            className="flex w-full items-center justify-center"
            style={{
              height: '60cqh',
              maxHeight: '100%',
            }}
          >
            <div className="flex h-full w-full items-center justify-center [&_img]:block [&_img]:h-full [&_img]:w-auto [&_img]:max-h-full [&_img]:max-w-full [&_img]:object-contain [&_svg]:max-h-full [&_svg]:max-w-full [&_svg]:object-contain">
              {leading}
            </div>
          </div>
        ) : null}
      </div>
      <div className="flex min-h-0 min-w-0 w-[80%] shrink-0 flex-row items-center justify-between gap-[2cqi]">
        <span className="min-w-0 truncate text-left text-white/70" style={fontStyle}>
          {label}
        </span>
        <span className="min-w-0 shrink-0 text-right font-medium text-amber-50/95" style={fontStyle}>
          {value}
        </span>
      </div>
    </div>
  )
}

/** Waiting modal footer — Confirm / Cancel labels share this; CloseIcon uses `1em` from the button. */
const AWAITING_JOIN_FOOTER_BUTTON_FONT_SIZE = 'clamp(14px, 8cqh, 20px)'

/** Parse numeric timestamps from socket JSON (may be string; rare mis-config may send seconds). */
function parseSocketUnixMs(value: unknown): number | null {
  if (value == null) return null
  const n =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && String(value).trim() !== ''
        ? Number(String(value).trim())
        : NaN
  if (!Number.isFinite(n)) return null
  let ms = Math.floor(n)
  if (ms > 0 && ms < 1e12) {
    ms *= 1000
  }
  return ms
}

export const GlobalDirectChallengeHost: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { address } = useWeb3Context()
  const incoming = useSelector((s: RootState) => s.app.pvp.incomingDirectChallenge)
  const respondBusy = useSelector((s: RootState) => s.app.pvp.directChallengeRespondBusy)
  const awaitingJoin = useSelector((s: RootState) => s.app.pvp.directChallengeAwaitingJoin)
  const directChallengeDeclinedMessage = useSelector((s: RootState) => s.app.pvp.directChallengeDeclinedMessage)
  const showAwaitingJoinModal = React.useMemo(() => {
    if (!awaitingJoin || !address?.trim()) return false
    const rw = String(awaitingJoin.requesterWallet || '').trim().toLowerCase()
    if (!rw) return false
    return rw === address.trim().toLowerCase()
  }, [awaitingJoin, address])
  const joinerLock = useSelector((s: RootState) => s.app.pvp.directChallengeJoinerLock)
  const activeRoomId = useSelector((s: RootState) => s.app.pvp.directChallengeActiveRoomId)
  const user = useSelector((s: RootState) => s.userModule.user)

  /** Battle-start overlay (`3…2…1`) — must be declared before `useServerNowMs` so we can gate the hook. */
  const [dcCountdownEndsAt, setDcCountdownEndsAt] = React.useState<number | null>(null)

  const directChallengeServerClockEnabled = React.useMemo(
    () =>
      Boolean(
        incoming ||
        dcCountdownEndsAt != null ||
        (awaitingJoin != null &&
          awaitingJoin.joinPrepExpiresAtUnixMs != null &&
          Number.isFinite(Number(awaitingJoin.joinPrepExpiresAtUnixMs))),
      ),
    [incoming, dcCountdownEndsAt, awaitingJoin],
  )

  /** `server-time-tick` on the PvP socket; used for join-prep countdown (not raw `Date.now()`). */
  const syncedServerNowMs = useServerNowMs(address, { enabled: directChallengeServerClockEnabled })

  const awaitingJoinSummaryColumns = React.useMemo(() => {
    if (!awaitingJoin) return null
    const bm = awaitingJoin.battleMode ?? 'unranked'
    const modeValue =
      bm === 'ranked' ? t('pvp.directChallengeModeRanked') : t('pvp.directChallengeModeUnranked')
    const amt = Number(awaitingJoin.stakeAmount)
    const stakeValue =
      bm !== 'ranked' || !Number.isFinite(amt) || amt <= 0
        ? t('pvp.directChallengeAwaitingSummaryNoStake')
        : awaitingJoin.stakeCurrency === 'ecs'
          ? `${amt} ${t('pvp.directChallengeCurrencyEcs')}`
          : `${amt} ${t('pvp.directChallengeCurrencyUsdt')}`
    const ccRaw = Number(awaitingJoin.characterCount)
    const cc = Number.isFinite(ccRaw) && ccRaw >= 1 && ccRaw <= 3 ? Math.floor(ccRaw) : 1
    const countValue = t('pvp.directChallengeIncomingVsFormat', { n: cc })
    return [
      { title: t('pvp.directChallengeAwaitingSummaryBattleMode'), value: modeValue },
      { title: t('pvp.directChallengeAwaitingSummaryStake'), value: stakeValue },
      { title: t('pvp.directChallengeAwaitingSummaryCharacterCount'), value: countValue },
    ] as const
  }, [awaitingJoin, t])

  const [cancelAwaitingJoinBusy, setCancelAwaitingJoinBusy] = React.useState(false)
  const [dcExpiredModal, setDcExpiredModal] = React.useState<{
    phase: 'pending' | 'join_prep'
    deferredClear: boolean
  } | null>(null)
  const [awaitingJoinSelfAvatarFailed, setAwaitingJoinSelfAvatarFailed] = React.useState(false)
  const [awaitingJoinOpponentAvatarFailed, setAwaitingJoinOpponentAvatarFailed] = React.useState(false)

  const dcCountdownSyncRef = React.useRef({ serverMs: 0, localMs: 0 })
  const [dcCountdownUiPulse, setDcCountdownUiPulse] = React.useState(0)
  const [requesterAvatarFailed, setRequesterAvatarFailed] = React.useState(false)
  const countdownNeedsPayRef = React.useRef(false)
  const countdownRoomIdRef = React.useRef<string | null>(null)
  const countdownChallengeIdRef = React.useRef<string | null>(null)
  const directChallengeFlowRef = React.useRef(false)
  const activeRoomIdRef = React.useRef<string | null>(null)
  const incomingPayoutAsideRef = React.useRef<HTMLDivElement>(null)
  const [incomingPayoutAsideSize, setIncomingPayoutAsideSize] = React.useState({ w: 0, h: 0 })
  const [acceptInsufficientBalanceTipOpen, setAcceptInsufficientBalanceTipOpen] = React.useState(false)
  /** Dedupe socket + client watchdog firing expiry twice for the same challenge/phase. */
  const dcExpiryDedupeRef = React.useRef<string | null>(null)

  const incomingAcceptInsufficientFunds = React.useMemo(() => {
    if (!incoming || incoming.battleMode !== 'ranked') return false
    const need = Number(incoming.stakeAmount)
    if (!Number.isFinite(need) || need <= 0) return false
    const isEcs = (incoming.stakeCurrency ?? 'usdt').toLowerCase() === 'ecs'
    const have = isEcs ? Number(user?.ecsTokenAmount ?? 0) : Number(user?.usdt ?? 0)
    return have < need
  }, [incoming, user?.ecsTokenAmount, user?.usdt])

  React.useEffect(() => {
    if (!incoming) setAcceptInsufficientBalanceTipOpen(false)
  }, [incoming])

  const incomingPayoutLayout = React.useMemo(() => {
    const { w, h } = incomingPayoutAsideSize
    if (w <= 0 || h <= 0) return null
    const padX = w * INCOMING_PAYOUT_ASIDE_PAD_W_FR
    const padY = h * INCOMING_PAYOUT_ASIDE_PAD_H_FR
    const gapPx = h * INCOMING_PAYOUT_ASIDE_GAP_FR
    const fontPx = h * INCOMING_PAYOUT_ASIDE_FONT_FR
    const titleFontPx = fontPx * INCOMING_PAYOUT_TITLE_VS_VALUE_FONT
    const iconPx = Math.round(h * INCOMING_PAYOUT_ASIDE_ICON_FR)
    const asideStyle: React.CSSProperties = {
      boxSizing: 'border-box',
      paddingLeft: padX,
      paddingRight: padX,
      paddingTop: padY,
      paddingBottom: padY,
      gap: gapPx,
      fontSize: `${fontPx}px`,
      lineHeight: 1.2,
      fontFamily: '"Segoe UI", system-ui, sans-serif',
    }
    const rowStyle: React.CSSProperties = {
      gap: Math.max(4, padX * 0.35),
    }
    const labelSpanStyle: React.CSSProperties = {
      fontSize: `${titleFontPx}px`,
      lineHeight: 1.15,
    }
    const valueStyle: React.CSSProperties = {
      fontFamily: "'Tilt Warp', sans-serif",
      fontSize: `${fontPx}px`,
      lineHeight: 1.1,
    }
    return { asideStyle, rowStyle, labelSpanStyle, valueStyle, iconPx }
  }, [incomingPayoutAsideSize])

  React.useEffect(() => {
    setAwaitingJoinSelfAvatarFailed(false)
    setAwaitingJoinOpponentAvatarFailed(false)
  }, [awaitingJoin?.challengeId])

  const joinPrepTimeSyncRef = React.useRef({ serverMs: 0, localMs: 0 })
  React.useLayoutEffect(() => {
    joinPrepTimeSyncRef.current = { serverMs: syncedServerNowMs, localMs: Date.now() }
  }, [syncedServerNowMs])

  /** Pulse so MM:SS re-renders every 250ms while the modal deadline is active (aligned with join-prep UX elsewhere). */
  const [joinPrepUiPulse, setJoinPrepUiPulse] = React.useState(0)
  React.useEffect(() => {
    if (!awaitingJoin?.joinPrepExpiresAtUnixMs && !incoming?.pendingExpiresAtUnixMs) return
    const id = window.setInterval(() => setJoinPrepUiPulse((n) => n + 1), 250)
    return () => window.clearInterval(id)
  }, [
    awaitingJoin?.challengeId,
    awaitingJoin?.joinPrepExpiresAtUnixMs,
    incoming?.challengeId,
    incoming?.pendingExpiresAtUnixMs,
  ])

  /**
   * Remaining time using server-aligned clocks only:
   * `ttlMs - (serverNow - challengeCreatedAtUnixMs)` with `ttlMs = joinPrepExpiresAtUnixMs - challengeCreatedAtUnixMs`,
   * which equals `joinPrepExpiresAtUnixMs - serverNow` when both deadlines and `createdAt` share the server epoch.
   */
  const joinPrepRemainingMs = React.useMemo(() => {
    if (!awaitingJoin?.joinPrepExpiresAtUnixMs) return 0
    const { serverMs, localMs } = joinPrepTimeSyncRef.current
    const estimatedServerNow =
      serverMs === 0 && localMs === 0
        ? syncedServerNowMs
        : serverMs + (Date.now() - localMs)

    const started = awaitingJoin.challengeCreatedAtUnixMs
    if (started != null && Number.isFinite(started)) {
      const ttlMs = awaitingJoin.joinPrepExpiresAtUnixMs - started
      if (ttlMs > 0) {
        return Math.max(0, ttlMs - (estimatedServerNow - started))
      }
    }
    return Math.max(0, awaitingJoin.joinPrepExpiresAtUnixMs - estimatedServerNow)
  }, [
    awaitingJoin?.joinPrepExpiresAtUnixMs,
    awaitingJoin?.challengeCreatedAtUnixMs,
    syncedServerNowMs,
    joinPrepUiPulse,
  ])

  /** Opponent incoming modal: pending challenge TTL (same clock math as join-prep countdown). */
  const incomingPendingRemainingMs = React.useMemo(() => {
    if (!incoming?.challengeId || !Number.isFinite(Number(incoming.pendingExpiresAtUnixMs))) {
      return Number.POSITIVE_INFINITY
    }
    const exp = Number(incoming.pendingExpiresAtUnixMs)
    const { serverMs, localMs } = joinPrepTimeSyncRef.current
    const estimatedServerNow =
      serverMs === 0 && localMs === 0
        ? syncedServerNowMs
        : serverMs + (Date.now() - localMs)

    const started =
      incoming.challengeCreatedAtUnixMs != null &&
        Number.isFinite(Number(incoming.challengeCreatedAtUnixMs))
        ? Number(incoming.challengeCreatedAtUnixMs)
        : null
    if (started != null) {
      const ttlMs = exp - started
      if (ttlMs > 0) {
        return Math.max(0, ttlMs - (estimatedServerNow - started))
      }
    }
    return Math.max(0, exp - estimatedServerNow)
  }, [
    incoming?.challengeId,
    incoming?.challengeCreatedAtUnixMs,
    incoming?.pendingExpiresAtUnixMs,
    syncedServerNowMs,
    joinPrepUiPulse,
  ])

  React.useEffect(() => {
    dcExpiryDedupeRef.current = null
  }, [awaitingJoin?.challengeId, incoming?.challengeId, joinerLock?.challengeId])

  const applyDirectChallengeExpired = React.useCallback(
    (data: { challengeId?: string; phase?: string }) => {
      const normCid = (x: unknown) => {
        if (x == null) return ''
        const s = String(x).trim()
        return s ? s.toLowerCase() : ''
      }
      const cid = normCid(data?.challengeId)
      if (!cid) return
      const phase: 'pending' | 'join_prep' = data?.phase === 'pending' ? 'pending' : 'join_prep'

      const p = store.getState().app.pvp
      const awaitingCid = normCid(p.directChallengeAwaitingJoin?.challengeId)
      const lockCid = normCid(p.directChallengeJoinerLock?.challengeId)
      const incomingCid = normCid(p.incomingDirectChallenge?.challengeId)
      const matches = awaitingCid === cid || lockCid === cid || incomingCid === cid
      if (!matches) return

      /** Pending TTL: challenged player still on incoming modal (never accepted) — dismiss only, no expiration modal. */
      const inc = p.incomingDirectChallenge
      if (phase === 'pending' && inc && normCid(inc.challengeId) === cid) {
        const self = normCid(address)
        const req = normCid(inc.requesterWallet)
        if (self && req && self !== req) {
          dispatch(setIncomingDirectChallenge(null))
          return
        }
      }

      const dedupeKey = `${cid}:${phase}`
      if (dcExpiryDedupeRef.current === dedupeKey) return
      dcExpiryDedupeRef.current = dedupeKey

      const joinerLockState = p.directChallengeJoinerLock
      const self = normCid(address)
      const reqW = normCid(joinerLockState?.requesterWallet)
      const isJoinerJoinPrepExpiry =
        phase === 'join_prep' &&
        lockCid === cid &&
        Boolean(joinerLockState) &&
        Boolean(self) &&
        Boolean(reqW) &&
        reqW !== self

      directChallengeFlowRef.current = false
      countdownRoomIdRef.current = null
      countdownChallengeIdRef.current = null
      countdownNeedsPayRef.current = false
      setDcCountdownEndsAt(null)
      dispatch(setDirectChallengeBattleStartPending(false))
      dispatch(setDirectChallengeActiveRoomId(null))

      if (isJoinerJoinPrepExpiry) {
        setDcExpiredModal({ phase: 'join_prep', deferredClear: true })
        return
      }

      dispatch(clearDirectChallengeFlow())
      setDcExpiredModal({ phase, deferredClear: false })
    },
    [address, dispatch],
  )

  const applyDirectChallengeCountdown = React.useCallback(
    (data: DirectChallengeCountdownDetail) => {
      const cid = data?.challengeId != null ? String(data.challengeId).trim() : ''
      if (!cid) return
      countdownChallengeIdRef.current = cid
      if (data.roomId) {
        countdownRoomIdRef.current = String(data.roomId)
        dispatch(setDirectChallengeActiveRoomId(String(data.roomId)))
      } else {
        countdownRoomIdRef.current = null
        dispatch(setDirectChallengeActiveRoomId(null))
      }
      countdownNeedsPayRef.current = Boolean(data.needsPayment)
      directChallengeFlowRef.current = true
      dispatch(setDirectChallengeBattleStartPending(true))
      const prevAj = store.getState().app.pvp.directChallengeAwaitingJoin
      if (prevAj) {
        dispatch(setDirectChallengeAwaitingJoin({ ...prevAj, countingDown: true }))
      }
      const fallbackSecs =
        typeof data.countdownSeconds === 'number' && Number.isFinite(data.countdownSeconds)
          ? data.countdownSeconds
          : 3
      const serverSnap =
        typeof data.serverNowMs === 'number' && Number.isFinite(data.serverNowMs)
          ? Math.floor(data.serverNowMs)
          : Date.now()
      const endsAt =
        typeof data.countdownEndsAtUnixMs === 'number' && Number.isFinite(data.countdownEndsAtUnixMs)
          ? Math.floor(data.countdownEndsAtUnixMs)
          : serverSnap + fallbackSecs * 1000
      dcCountdownSyncRef.current = { serverMs: serverSnap, localMs: Date.now() }
      setDcCountdownEndsAt(endsAt)
    },
    [dispatch],
  )

  React.useEffect(() => {
    if (!address?.trim()) return
    if (!awaitingJoin?.challengeId) return
    if (!Number.isFinite(awaitingJoin.joinPrepExpiresAtUnixMs)) return
    if (directChallengeDeclinedMessage) return
    if (awaitingJoin.countingDown) return
    /** Join-prep expiry via `directChallengeAwaitingJoin` applies to the challenger only; joiner uses `joinerLock` + socket expiry. */
    const rw = String(awaitingJoin.requesterWallet || '').trim().toLowerCase()
    const self = address.trim().toLowerCase()
    if (!rw || rw !== self) return
    if (joinPrepRemainingMs > 0) return
    const phase: 'pending' | 'join_prep' = awaitingJoin.awaitingAcceptDecline ? 'pending' : 'join_prep'
    applyDirectChallengeExpired({
      challengeId: awaitingJoin.challengeId,
      phase,
    })
  }, [
    address,
    awaitingJoin?.challengeId,
    awaitingJoin?.awaitingAcceptDecline,
    awaitingJoin?.countingDown,
    awaitingJoin?.joinPrepExpiresAtUnixMs,
    awaitingJoin?.requesterWallet,
    applyDirectChallengeExpired,
    directChallengeDeclinedMessage,
    joinPrepRemainingMs,
  ])

  React.useEffect(() => {
    if (!address?.trim()) return
    if (!incoming?.challengeId) return
    if (!Number.isFinite(Number(incoming.pendingExpiresAtUnixMs))) return
    if (incomingPendingRemainingMs > 0) return
    applyDirectChallengeExpired({
      challengeId: incoming.challengeId,
      phase: 'pending',
    })
  }, [
    address,
    applyDirectChallengeExpired,
    incoming?.challengeId,
    incoming?.pendingExpiresAtUnixMs,
    incomingPendingRemainingMs,
  ])

  React.useEffect(() => {
    if (dcCountdownEndsAt == null) return
    const id = window.setInterval(() => setDcCountdownUiPulse((n) => n + 1), 250)
    return () => window.clearInterval(id)
  }, [dcCountdownEndsAt])

  const dcBattleStartRemainingMs = React.useMemo(() => {
    if (dcCountdownEndsAt == null) return 0
    const { serverMs, localMs } = dcCountdownSyncRef.current
    const estimatedServerNow = serverMs + (Date.now() - localMs)
    return Math.max(0, dcCountdownEndsAt - estimatedServerNow)
  }, [dcCountdownEndsAt, dcCountdownUiPulse])

  /** Whole seconds remaining until server deadline (ceil); 0 means show “Starting…” until socket clears overlay. */
  const dcCountdownDisplaySec = Math.max(0, Math.ceil(dcBattleStartRemainingMs / 1000))

  React.useEffect(() => {
    setRequesterAvatarFailed(false)
  }, [incoming?.challengeId])

  React.useEffect(() => {
    activeRoomIdRef.current = activeRoomId
  }, [activeRoomId])

  const joinerLockRef = React.useRef(joinerLock)
  React.useEffect(() => {
    joinerLockRef.current = joinerLock
  }, [joinerLock])

  React.useEffect(() => {
    if (!incoming || incoming.battleMode !== 'ranked') {
      setIncomingPayoutAsideSize({ w: 0, h: 0 })
      return
    }
    let cancelled = false
    let ro: ResizeObserver | undefined
    const setup = () => {
      const el = incomingPayoutAsideRef.current
      if (!el || typeof ResizeObserver === 'undefined' || cancelled) return
      const measure = () => {
        const r = el.getBoundingClientRect()
        setIncomingPayoutAsideSize({ w: r.width, h: r.height })
      }
      measure()
      ro = new ResizeObserver(measure)
      ro.observe(el)
    }
    requestAnimationFrame(() => requestAnimationFrame(setup))
    return () => {
      cancelled = true
      ro?.disconnect()
    }
  }, [incoming?.battleMode, incoming?.challengeId])

  React.useEffect(() => {
    if (address) return
    dispatch(clearDirectChallengeFlow())
    setDcCountdownEndsAt(null)
    setDcExpiredModal(null)
    countdownRoomIdRef.current = null
    countdownChallengeIdRef.current = null
    countdownNeedsPayRef.current = false
    directChallengeFlowRef.current = false
  }, [address, dispatch])

  React.useEffect(() => {
    if (!address) return
    const socket = getSharedPvPSocket(address)
    const onConnect = () => {
      socket.emit('join-wallet-room', { walletAddress: address })
      socket.emit('user:online', { walletAddress: address })
    }

    const onIncoming = (data: PvpIncomingDirectChallengeRequest) => {
      dispatch(setDirectChallengeDeclinedMessage(null))
      dispatch(setIncomingDirectChallenge(data))
    }

    const onAccepted = (data: {
      challengeId?: string
      requesterWallet?: string
      opponentNickname?: string
      opponentWallet?: string
      opponentAvatarUrl?: string | null
      opponentUserLvl?: number
      opponentPvpMmr?: number
      serverNowMs?: number
      joinPrepExpiresAtUnixMs?: number | string
      challengeCreatedAtUnixMs?: number | string
      battleMode?: 'ranked' | 'unranked'
      stakeCurrency?: 'usdt' | 'ecs'
      stakeAmount?: number
      characterCount?: number
    }) => {
      const cid = data?.challengeId
      if (!cid) return
      const requesterWallet = String(data?.requesterWallet ?? '')
        .trim()
        .toLowerCase()
      if (!requesterWallet) return
      const self = address?.trim().toLowerCase() ?? ''
      const nowMs = Date.now()
      const fromJoin = parseSocketUnixMs(data.joinPrepExpiresAtUnixMs)
      const fromServerNow = parseSocketUnixMs(data.serverNowMs)
      let joinPrepExpiresAtUnixMs =
        fromJoin != null
          ? fromJoin
          : fromServerNow != null
            ? fromServerNow + 180_000
            : nowMs + 180_000
      const prevAwait = store.getState().app.pvp.directChallengeAwaitingJoin
      const rawPrev = prevAwait?.challengeId === cid ? prevAwait.joinPrepExpiresAtUnixMs : null
      const prevExp = typeof rawPrev === 'number' && Number.isFinite(rawPrev) ? rawPrev : null
      /**
       * Only clamp with a prior deadline when it is still in the future and the prior slice was
       * already join-prep (not the pending accept/decline window — that would force 0:00 after accept).
       */
      if (prevExp != null && prevExp > nowMs && !prevAwait?.awaitingAcceptDecline) {
        joinPrepExpiresAtUnixMs = Math.min(joinPrepExpiresAtUnixMs, prevExp)
      }
      if (joinPrepExpiresAtUnixMs <= nowMs) {
        joinPrepExpiresAtUnixMs = nowMs + 180_000
      }
      const battleMode = data?.battleMode === 'ranked' ? 'ranked' : 'unranked'
      const rawCc = Number(data?.characterCount)
      const characterCount =
        Number.isFinite(rawCc) && rawCc >= 1 && rawCc <= 3 ? Math.floor(rawCc) : 1
      const stakeAmount = Number(data?.stakeAmount)
      const stakeCurrency =
        data?.stakeCurrency === 'ecs' ? 'ecs' : data?.stakeCurrency === 'usdt' ? 'usdt' : null
      const challengeCreatedAtUnixMs = parseSocketUnixMs(data.challengeCreatedAtUnixMs) ?? undefined
      dispatch(setDirectChallengeDeclinedMessage(null))
      dispatch(
        setDirectChallengeAwaitingJoin({
          challengeId: cid,
          requesterWallet,
          opponentNickname: String(data?.opponentNickname ?? '').trim(),
          opponentWallet: String(data?.opponentWallet ?? '')
            .trim()
            .toLowerCase(),
          opponentAvatarUrl: data?.opponentAvatarUrl ?? null,
          opponentUserLvl:
            data?.opponentUserLvl != null && Number.isFinite(Number(data.opponentUserLvl))
              ? Math.max(1, Math.floor(Number(data.opponentUserLvl)))
              : undefined,
          opponentPvpMmr:
            data?.opponentPvpMmr != null && Number.isFinite(Number(data.opponentPvpMmr))
              ? Math.floor(Number(data.opponentPvpMmr))
              : undefined,
          battleMode,
          stakeCurrency: battleMode === 'ranked' ? stakeCurrency : null,
          stakeAmount: battleMode === 'ranked' && Number.isFinite(stakeAmount) ? stakeAmount : 0,
          characterCount,
          joinPrepExpiresAtUnixMs,
          ...(challengeCreatedAtUnixMs != null ? { challengeCreatedAtUnixMs } : {}),
          awaitingAcceptDecline: false,
        }),
      )
      /** Challenger waits on the send page + global modal; the accepted roster page is joiner-only. */
      if (self && self === requesterWallet && location.pathname.includes('/pvp/direct-challenge/accepted')) {
        navigate('/pvp/direct-challenge', { replace: true })
      }
    }

    const onDeclined = () => {
      dispatch(setIncomingDirectChallenge(null))
      dispatch(setDirectChallengeRespondBusy(false))
      const p = store.getState().app.pvp
      const aj = p.directChallengeAwaitingJoin
      const self = address?.trim().toLowerCase() ?? ''
      const isRequesterWaiting =
        Boolean(aj && self && String(aj.requesterWallet).trim().toLowerCase() === self)
      if (isRequesterWaiting) {
        dispatch(setDirectChallengeDeclinedMessage(t('pvp.directChallengeOpponentDeclined')))
      } else {
        dispatch(setDirectChallengeAwaitingJoin(null))
        dispatch(setDirectChallengeDeclinedMessage(null))
      }
    }

    const onLegacyJoinPrepExpiredSocket = (data: { challengeId?: string }) => {
      applyDirectChallengeExpired({ challengeId: data?.challengeId, phase: 'join_prep' })
    }

    const onCountdown = (data: DirectChallengeCountdownDetail) => {
      applyDirectChallengeCountdown(data)
    }

    const onRoomReady = (data: {
      challengeId?: string
      roomId?: string
      needsPayment?: boolean
      stakeAmount?: number
      stakeCurrency?: 'usdt' | 'ecs'
    }) => {
      const rid = data?.roomId != null ? String(data.roomId).trim() : ''
      if (!rid) return
      const cid = data?.challengeId != null ? String(data.challengeId).trim() : ''
      if (
        cid &&
        countdownChallengeIdRef.current &&
        cid !== countdownChallengeIdRef.current
      ) {
        return
      }
      countdownRoomIdRef.current = rid
      dispatch(setDirectChallengeActiveRoomId(rid))
      const lock = joinerLockRef.current
      const stakeNum = Number(data?.stakeAmount)
      if (
        lock &&
        cid &&
        lock.challengeId === cid &&
        Number.isFinite(stakeNum) &&
        stakeNum > 0
      ) {
        dispatch(
          setDirectChallengeJoinerLock({
            ...lock,
            stakeAmount: stakeNum,
            stakeCurrency: data.stakeCurrency ?? lock.stakeCurrency ?? null,
          }),
        )
      }
      if (data.needsPayment && address) {
        void postPvpProcessPayment(address, rid)
      }
      setDcCountdownEndsAt(null)
    }

    const onStartFailed = (data: { challengeId?: string }) => {
      const cid = data?.challengeId != null ? String(data.challengeId).trim() : ''
      if (
        cid &&
        countdownChallengeIdRef.current &&
        cid !== countdownChallengeIdRef.current
      ) {
        return
      }
      directChallengeFlowRef.current = false
      countdownRoomIdRef.current = null
      countdownChallengeIdRef.current = null
      countdownNeedsPayRef.current = false
      dispatch(clearDirectChallengeFlow())
      setDcCountdownEndsAt(null)
      void alert.open(t('pvp.directChallengeStartFailed'))
    }

    const onCancelledByRequester = (data: { challengeId?: string }) => {
      const cid = data?.challengeId
      if (!cid) return
      const incoming = store.getState().app.pvp.incomingDirectChallenge
      if (incoming?.challengeId === cid) {
        dispatch(setIncomingDirectChallenge(null))
      }
      const lock = joinerLockRef.current
      if (lock?.challengeId === cid) {
        dispatch(setDirectChallengeJoinerLock(null))
        void alert.open(t('pvp.directChallengeRequesterCancelled'))
      }
      if (countdownChallengeIdRef.current === cid) {
        directChallengeFlowRef.current = false
        countdownRoomIdRef.current = null
        countdownChallengeIdRef.current = null
        countdownNeedsPayRef.current = false
        dispatch(setDirectChallengeActiveRoomId(null))
        dispatch(setDirectChallengeBattleStartPending(false))
        setDcCountdownEndsAt(null)
      }
    }

    const onBattleStarted = async (data: { roomId?: string; battleId?: string }) => {
      const rid = data?.roomId != null ? String(data.roomId).trim() : ''
      const bid = data?.battleId != null ? String(data.battleId).trim() : ''
      if (!rid || !bid) return

      const dcPending = store.getState().app.pvp.directChallengeBattleStartPending
      const inDirectChallengeBattleFlow =
        dcPending ||
        directChallengeFlowRef.current ||
        Boolean(countdownChallengeIdRef.current)

      if (!inDirectChallengeBattleFlow) return

      const expectedRaw = activeRoomIdRef.current || countdownRoomIdRef.current
      const expected =
        expectedRaw != null && String(expectedRaw).trim() !== ''
          ? String(expectedRaw).trim()
          : ''
      if (expected && rid !== expected) return

      directChallengeFlowRef.current = false
      try {
        const res = await getPvPRoomDetails(address, rid)
        if (res.success && res.room) dispatch(setCurrentRoom(res.room as PvPRoom))
      } catch {
        /* ignore */
      }

      dispatch(clearDirectChallengeFlow())
      countdownRoomIdRef.current = null
      countdownChallengeIdRef.current = null
      countdownNeedsPayRef.current = false
      setDcCountdownEndsAt(null)
      navigate(`/pvp/battle/${bid}?roomId=${encodeURIComponent(rid)}`)
    }

    socket.on('connect', onConnect)
    socket.on('pvp:direct-challenge-request', onIncoming)
    socket.on('pvp:direct-challenge-accepted', onAccepted)
    socket.on('pvp:direct-challenge-declined', onDeclined)
    socket.on('pvp:direct-challenge-expired', applyDirectChallengeExpired)
    socket.on('pvp:direct-challenge-join-prep-expired', onLegacyJoinPrepExpiredSocket)
    socket.on('pvp:direct-challenge-countdown', onCountdown)
    socket.on('pvp:direct-challenge-room-ready', onRoomReady)
    socket.on('pvp:direct-challenge-start-failed', onStartFailed)
    socket.on('pvp:direct-challenge-cancelled-by-requester', onCancelledByRequester)
    socket.on('pvp:battle-started', onBattleStarted)

    if (socket.connected) onConnect()

    return () => {
      socket.off('connect', onConnect)
      socket.off('pvp:direct-challenge-request', onIncoming)
      socket.off('pvp:direct-challenge-accepted', onAccepted)
      socket.off('pvp:direct-challenge-declined', onDeclined)
      socket.off('pvp:direct-challenge-expired', applyDirectChallengeExpired)
      socket.off('pvp:direct-challenge-join-prep-expired', onLegacyJoinPrepExpiredSocket)
      socket.off('pvp:direct-challenge-countdown', onCountdown)
      socket.off('pvp:direct-challenge-room-ready', onRoomReady)
      socket.off('pvp:direct-challenge-start-failed', onStartFailed)
      socket.off('pvp:direct-challenge-cancelled-by-requester', onCancelledByRequester)
      socket.off('pvp:battle-started', onBattleStarted)
    }
  }, [address, applyDirectChallengeCountdown, applyDirectChallengeExpired, dispatch, location.pathname, navigate, t])

  React.useEffect(() => {
    const onLocalCountdown = (event: Event) => {
      const detail = (event as CustomEvent<DirectChallengeCountdownDetail>).detail
      if (!detail) return
      applyDirectChallengeCountdown(detail)
    }
    window.addEventListener(DIRECT_CHALLENGE_COUNTDOWN_EVENT, onLocalCountdown)
    return () => window.removeEventListener(DIRECT_CHALLENGE_COUNTDOWN_EVENT, onLocalCountdown)
  }, [applyDirectChallengeCountdown])

  const handleAccept = React.useCallback(async () => {
    if (!address || !incoming || respondBusy) return
    if (incoming.battleMode === 'ranked') {
      const need = Number(incoming.stakeAmount)
      if (Number.isFinite(need) && need > 0) {
        const u = store.getState().userModule.user
        const isEcs = (incoming.stakeCurrency ?? 'usdt').toLowerCase() === 'ecs'
        const have = isEcs ? Number(u?.ecsTokenAmount ?? 0) : Number(u?.usdt ?? 0)
        if (have < need) return
      }
    }
    dispatch(setDirectChallengeRespondBusy(true))
    const res = await postPvpDirectChallengeAccept({
      walletAddress: address,
      challengeId: incoming.challengeId,
    })
    dispatch(setDirectChallengeRespondBusy(false))
    if (!res.success) {
      alert.open(res.message || t('pvp.directChallengeAcceptFailed'))
      return
    }
    const acceptData = res.data
    if (acceptData && acceptData.joinPrepExpiresAtUnixMs > 0) {
      dispatch(
        setDirectChallengeAwaitingJoin({
          challengeId: incoming.challengeId,
          requesterWallet: String(acceptData.requesterWallet || incoming.requesterWallet)
            .trim()
            .toLowerCase(),
          opponentNickname:
            String(acceptData.opponentNickname || '').trim() ||
            (address ? `${address.slice(0, 6)}…${address.slice(-4)}` : ''),
          opponentWallet: String(acceptData.opponentWallet || address).trim().toLowerCase(),
          opponentAvatarUrl: acceptData.opponentAvatarUrl ?? userAvatarImageUrl(user) ?? null,
          opponentUserLvl: acceptData.opponentUserLvl,
          opponentPvpMmr: acceptData.opponentPvpMmr,
          battleMode: incoming.battleMode,
          stakeCurrency:
            incoming.battleMode === 'ranked'
              ? incoming.stakeCurrency === 'ecs'
                ? 'ecs'
                : 'usdt'
              : null,
          stakeAmount: incoming.battleMode === 'ranked' ? Number(incoming.stakeAmount) : 0,
          characterCount: incoming.characterCount,
          joinPrepExpiresAtUnixMs: acceptData.joinPrepExpiresAtUnixMs,
          ...(acceptData.challengeCreatedAtUnixMs != null
            ? { challengeCreatedAtUnixMs: acceptData.challengeCreatedAtUnixMs }
            : {}),
          awaitingAcceptDecline: false,
        }),
      )
    }
    dispatch(
      setDirectChallengeJoinerLock({
        challengeId: incoming.challengeId,
        requesterWallet: String(incoming.requesterWallet).trim().toLowerCase(),
        requesterNickname: incoming.requesterNickname,
        requesterAvatarUrl: incoming.requesterAvatarUrl ?? null,
        requesterPvpMmr:
          incoming.requesterPvpMmr != null && Number.isFinite(Number(incoming.requesterPvpMmr))
            ? Math.floor(Number(incoming.requesterPvpMmr))
            : undefined,
        requesterUserLvl:
          incoming.requesterUserLvl != null && Number.isFinite(Number(incoming.requesterUserLvl))
            ? Math.max(1, Math.floor(Number(incoming.requesterUserLvl)))
            : undefined,
        battleMode: incoming.battleMode,
        stakeCurrency: incoming.stakeCurrency ?? null,
        stakeAmount: incoming.stakeAmount,
        characterCount: incoming.characterCount,
      }),
    )
    dispatch(setIncomingDirectChallenge(null))
    navigate('/pvp/direct-challenge/accepted')
  }, [address, dispatch, incoming, navigate, respondBusy, t, user])

  const handleDecline = React.useCallback(async () => {
    if (!address || !incoming || respondBusy) return
    dispatch(setDirectChallengeRespondBusy(true))
    const res = await postPvpDirectChallengeDecline({
      walletAddress: address,
      challengeId: incoming.challengeId,
    })
    dispatch(setDirectChallengeRespondBusy(false))
    if (!res.success) {
      alert.open(res.message || t('pvp.directChallengeDeclineFailed'))
      return
    }
    dispatch(setIncomingDirectChallenge(null))
  }, [address, dispatch, incoming, respondBusy, t])

  const handleCancelAwaitingJoin = React.useCallback(async () => {
    if (!address || !awaitingJoin?.challengeId || cancelAwaitingJoinBusy || !showAwaitingJoinModal) return
    if (!directChallengeDeclinedMessage && !awaitingJoin.awaitingAcceptDecline) return
    if (directChallengeDeclinedMessage) {
      dispatch(clearDirectChallengeFlow())
      return
    }
    setCancelAwaitingJoinBusy(true)
    const res = await postPvpDirectChallengeCancel({
      walletAddress: address,
      challengeId: awaitingJoin.challengeId,
    })
    setCancelAwaitingJoinBusy(false)
    if (!res.success) {
      void alert.open(res.message || t('pvp.directChallengeCancelFailed'))
      return
    }
    dispatch(setDirectChallengeAwaitingJoin(null))
  }, [
    address,
    awaitingJoin?.challengeId,
    awaitingJoin?.awaitingAcceptDecline,
    cancelAwaitingJoinBusy,
    directChallengeDeclinedMessage,
    dispatch,
    showAwaitingJoinModal,
    t,
  ])

  const handleDismissDirectChallengeExpiredModal = React.useCallback(() => {
    setDcExpiredModal((m) => {
      if (m?.deferredClear) {
        dispatch(clearDirectChallengeFlow())
        navigate('/pvp')
      }
      return null
    })
  }, [dispatch, navigate])

  const waitingModalSelfNickname = React.useMemo(
    () =>
      (user?.nickName && String(user.nickName).trim()) ||
      (address ? `${address.slice(0, 6)}…${address.slice(-4)}` : ''),
    [user?.nickName, address],
  )

  const waitingModalSelfAvatarUrl = React.useMemo(() => userAvatarImageUrl(user), [user])

  const waitingModalSelfLevel = React.useMemo(
    () =>
      user?.userLvl != null && Number.isFinite(Number(user.userLvl))
        ? Math.max(1, Math.floor(Number(user.userLvl)))
        : 1,
    [user?.userLvl],
  )

  const waitingModalSelfMmr = React.useMemo(
    () =>
      user?.pvpMmr != null && Number.isFinite(Number(user.pvpMmr))
        ? Math.floor(Number(user.pvpMmr))
        : 0,
    [user?.pvpMmr],
  )

  const incomingAvatarUrl = (incoming?.requesterAvatarUrl ?? '').trim()
  const showIncomingAvatarPhoto = Boolean(incomingAvatarUrl) && !requesterAvatarFailed
  const requesterLevel =
    incoming?.requesterUserLvl != null && Number.isFinite(Number(incoming.requesterUserLvl))
      ? Math.max(1, Math.floor(Number(incoming.requesterUserLvl)))
      : 1
  const requesterMmrDisplay =
    incoming?.requesterPvpMmr != null && Number.isFinite(Number(incoming.requesterPvpMmr))
      ? Math.floor(Number(incoming.requesterPvpMmr))
      : 0
  if (!address) return null

  return (
    <>
      {incoming ? (
        <>
          <div
            className="fixed inset-0 z-[85] bg-black/70 backdrop-blur-[2px]"
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="direct-challenge-incoming-title"
            className="fixed left-1/2 top-1/2 z-[86] flex min-h-0 -translate-x-1/2 -translate-y-1/2 flex-col overflow-visible rounded-xl border border-amber-400/40 bg-gradient-to-b from-zinc-900/98 to-black/95 text-left shadow-[0_16px_48px_rgba(0,0,0,0.65)]"
            style={{
              /** Largest 8×6 box inside min(70vw,800)×min(70vh,600); width = min(maxW, maxH×8/6). */
              width: 'min(70vw, 800px, calc(min(70vh, 600px) * 8 / 6))',
              aspectRatio: '8 / 6',
              height: 'auto',
              maxHeight: 'min(70vh, 600px)',
              boxSizing: 'border-box',
              containerType: 'size',
              containerName: 'dc-incoming-modal',
            }}
          >
            <div className="pointer-events-none absolute left-1/2 top-0 z-[1] flex w-full -translate-x-1/2 -translate-y-1/2 flex-col items-center px-[3%]">
              <img
                src={incoming.battleMode === 'ranked' ? RANKED_MARK_URL : UNRANKED_MARK_URL}
                alt=""
                className="object-contain"
                style={{ height: '20cqh', width: 'auto', maxWidth: '100%' }}
                draggable={false}
                loading="eager"
              />
              <div className="mt-[3cqh] box-border flex w-full min-w-0 flex-row items-center justify-center gap-[0.35em] px-[2%] sm:gap-[0.5em]">
                <img
                  src={PVP_LOBBY_PAGE_TITLE_DIVIDER_LEFT_URL}
                  alt=""
                  aria-hidden
                  className="min-h-0 min-w-0 shrink object-contain"
                  style={{ height: '8cqh', width: 'auto', maxWidth: '30%' }}
                  draggable={false}
                  loading="eager"
                />
                <h2
                  id="direct-challenge-incoming-title"
                  className="shrink-0 whitespace-nowrap text-center font-extrabold uppercase leading-none tracking-tight"
                  style={{
                    ...DIRECT_CHALLENGE_MODAL_TITLE_STYLE,
                    fontSize: '8cqh',
                    lineHeight: 1,
                  }}
                >
                  {t('pvp.directChallengePageTitle')}
                </h2>
                <img
                  src={PVP_LOBBY_PAGE_TITLE_DIVIDER_RIGHT_URL}
                  alt=""
                  aria-hidden
                  className="min-h-0 min-w-0 shrink object-contain"
                  style={{ height: '8cqh', width: 'auto', maxWidth: '30%' }}
                  draggable={false}
                  loading="eager"
                />
              </div>
            </div>

            <div
              className="box-border flex min-h-0 w-full flex-1 flex-col"
              style={{
                /** 3% of modal width / height; extra top inset clears the overlapping title band. */
                paddingLeft: '3cqi',
                paddingRight: '3cqi',
                paddingBottom: '3cqh',
                paddingTop: 'calc(3cqh + 16cqh)',
              }}
            >
              <div
                className="box-border flex w-full min-h-0 shrink-0 flex-row items-stretch"
                style={{
                  /** User info strip: 5% shorter than prior 25cqh; establishes cq units for this band. */
                  height: 'calc(25cqh * 0.95)',
                  minHeight: 'calc(25cqh * 0.95)',
                  gap: '5cqi',
                  containerType: 'size',
                  paddingTop: '1cqh',
                }}
              >
                <div className="flex h-full min-h-0 w-[20%] shrink-0 items-center justify-center">
                  <div className="box-border h-full max-h-full aspect-square max-w-full overflow-hidden rounded-full border-2 border-amber-400/75 bg-zinc-900/90">
                    {showIncomingAvatarPhoto ? (
                      <img
                        src={incomingAvatarUrl}
                        alt=""
                        className="h-full w-full object-cover"
                        draggable={false}
                        onError={() => setRequesterAvatarFailed(true)}
                      />
                    ) : (
                      <div
                        className="flex h-full w-full select-none items-center justify-center bg-zinc-800/90 text-[clamp(0.75rem,6cqh,2rem)] font-semibold text-amber-100/95"
                        aria-hidden
                      >
                        {requesterAvatarInitial(incoming.requesterNickname, incoming.requesterWallet)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex h-full min-h-0 min-w-0 flex-1 flex-row">
                  <div className="flex h-full min-h-0 w-[50%] min-w-0 flex-col">
                    <div
                      className="flex min-h-0 items-center justify-start overflow-hidden"
                      style={{ height: '60%', containerType: 'size' }}
                    >
                      <span
                        className="min-w-0 truncate text-left font-semibold text-white/95"
                        style={{
                          fontSize: '40cqh',
                          fontFamily: '"Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, "Roboto", sans-serif',
                        }}
                      >
                        {incoming.requesterNickname}
                      </span>
                    </div>
                    <div
                      className="flex min-h-0 items-center justify-start"
                      style={{ height: '40%', containerType: 'size' }}
                    >
                      <span
                        className="text-left text-white/85"
                        style={{
                          fontSize: '40cqh',
                          fontFamily: "'Tilt Warp', sans-serif",
                        }}
                      >
                        {t('pvp.directChallengeIncomingLevelLabel', { level: requesterLevel })}
                      </span>
                    </div>
                  </div>

                  <div
                    className="flex h-full min-h-0 w-[50%] min-w-0 flex-col justify-end"
                    style={{ containerType: 'size' }}
                  >
                    <div
                      className="flex w-full flex-col items-end justify-end"
                      style={{ rowGap: '10cqh' }}
                    >
                      <div className="flex w-full min-w-0 flex-row flex-wrap items-baseline justify-end gap-[1.5cqi]">
                        <span className="shrink-0 text-white/70" style={{ fontSize: '12cqh' }}>
                          {t('pvp.directChallengeIncomingMmRTitle')}
                        </span>
                        <span
                          className="font-semibold tabular-nums text-amber-100/95"
                          style={{ fontSize: '12cqh' }}
                        >
                          {requesterMmrDisplay.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="mt-[2cqh] grid min-h-0 w-full min-w-0 flex-1"
                style={{
                  gridTemplateColumns: '60% 35%',
                  columnGap: '5%',
                  alignContent: 'stretch',
                  minHeight: 0,
                }}
              >
                <section
                  className="box-border flex min-h-0 min-w-0 flex-col rounded-lg border border-amber-400/25 bg-black/30 px-[2cqi] pb-[2cqi] pt-[1cqh]"
                  style={{ containerType: 'size' }}
                >
                  <IncomingModalSectionTitle>{t('pvp.directChallengeBattleSettings')}</IncomingModalSectionTitle>
                  <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
                    <div
                      className="grid min-h-0 w-full min-w-0 flex-1"
                      style={{
                        gridTemplateRows:
                          incoming.battleMode === 'ranked'
                            ? 'minmax(0, 1fr) minmax(0, 1fr)'
                            : 'minmax(0, 1fr)',
                        rowGap: '1.5%',
                        minHeight: 0,
                      }}
                    >
                      <div className="flex min-h-0 min-w-0 flex-col overflow-hidden">
                        <IncomingBattleSettingRow
                          leading={<PartyFormatIcons count={3} iconCqh={34} />}
                          label={t('pvp.directChallengeCharacterCountHeading')}
                          value={t('pvp.directChallengeIncomingVsFormat', {
                            n: incoming.characterCount,
                          })}
                        />
                      </div>
                      {incoming.battleMode === 'ranked' ? (
                        <div className="flex min-h-0 min-w-0 flex-col overflow-hidden">
                          <IncomingBattleSettingRow
                            leading={
                              <img
                                src={
                                  (incoming.stakeCurrency ?? 'usdt').toLowerCase() === 'ecs'
                                    ? INCOMING_STAKE_ECS_ICON_URL
                                    : INCOMING_STAKE_USDT_ICON_URL
                                }
                                alt=""
                                aria-hidden
                                className="max-h-full max-w-full object-contain"
                                draggable={false}
                              />
                            }
                            label={t('pvp.directChallengeStakeAmount')}
                            value={`${incoming.stakeAmount} ${(incoming.stakeCurrency ?? 'usdt').toUpperCase()}`}
                          />
                        </div>
                      ) : null}
                    </div>
                  </div>
                </section>

                <section
                  className="box-border flex min-h-0 min-w-0 flex-col rounded-lg border border-amber-400/25 bg-black/30 px-[2cqi] pb-[2cqi] pt-[1cqh]"
                  style={{ containerType: 'size' }}
                >
                  <IncomingModalSectionTitle>
                    {t('pvp.directChallengeIncomingPayoutPerPlayerTitle')}
                  </IncomingModalSectionTitle>
                  {incoming.battleMode === 'ranked' ? (
                    <div
                      ref={incomingPayoutAsideRef}
                      role="complementary"
                      className={
                        'flex min-h-0 min-w-0 flex-1 flex-col justify-center self-stretch rounded-lg border border-white/10 bg-black/25 text-left' +
                        (incomingPayoutLayout ? '' : ' gap-2.5 p-2.5 sm:gap-3 sm:p-3')
                      }
                      style={
                        incomingPayoutLayout
                          ? incomingPayoutLayout.asideStyle
                          : { fontFamily: '"Segoe UI", system-ui, sans-serif' }
                      }
                    >
                      <div
                        className="flex items-center justify-between"
                        style={incomingPayoutLayout?.rowStyle}
                      >
                        <div
                          className="flex min-w-0 items-center"
                          style={incomingPayoutLayout?.rowStyle}
                        >
                          <img
                            src={pvpWinnerIconUrl}
                            alt=""
                            className={`shrink-0 object-contain ${incomingPayoutLayout ? '' : 'h-6 w-6 sm:h-7 sm:w-7'}`}
                            style={
                              incomingPayoutLayout
                                ? {
                                  width: incomingPayoutLayout.iconPx,
                                  height: incomingPayoutLayout.iconPx,
                                }
                                : undefined
                            }
                            draggable={false}
                          />
                          <span
                            className="truncate font-semibold tracking-wide text-white/95"
                            style={incomingPayoutLayout?.labelSpanStyle}
                          >
                            {t('pvp.directChallengeStakeWinnerTitle')}
                          </span>
                        </div>
                        <span
                          className="shrink-0 text-right font-bold tabular-nums text-emerald-400"
                          style={incomingPayoutLayout?.valueStyle}
                        >
                          {INCOMING_PAYOUT_WINNER_MULT}
                        </span>
                      </div>
                      <div
                        className="flex items-center justify-between"
                        style={incomingPayoutLayout?.rowStyle}
                      >
                        <div
                          className="flex min-w-0 items-center"
                          style={incomingPayoutLayout?.rowStyle}
                        >
                          <img
                            src={pvpLoserIconUrl}
                            alt=""
                            className={`shrink-0 object-contain ${incomingPayoutLayout ? '' : 'h-6 w-6 sm:h-7 sm:w-7'}`}
                            style={
                              incomingPayoutLayout
                                ? {
                                  width: incomingPayoutLayout.iconPx,
                                  height: incomingPayoutLayout.iconPx,
                                }
                                : undefined
                            }
                            draggable={false}
                          />
                          <span
                            className="truncate font-semibold tracking-wide text-white/95"
                            style={incomingPayoutLayout?.labelSpanStyle}
                          >
                            {t('pvp.directChallengeStakeLoserTitle')}
                          </span>
                        </div>
                        <span
                          className="shrink-0 text-right font-bold tabular-nums text-red-400"
                          style={incomingPayoutLayout?.valueStyle}
                        >
                          {INCOMING_PAYOUT_LOSER_MULT}
                        </span>
                      </div>
                      <div
                        className="flex items-center justify-between"
                        style={incomingPayoutLayout?.rowStyle}
                      >
                        <div
                          className="flex min-w-0 items-center"
                          style={incomingPayoutLayout?.rowStyle}
                        >
                          <img
                            src={INCOMING_STAKE_PAYOUT_PLATFORM_ICON_URL}
                            alt=""
                            className={`shrink-0 rounded object-contain ${incomingPayoutLayout ? '' : 'h-6 w-6 sm:h-7 sm:w-7'}`}
                            style={
                              incomingPayoutLayout
                                ? {
                                  width: incomingPayoutLayout.iconPx,
                                  height: incomingPayoutLayout.iconPx,
                                }
                                : undefined
                            }
                            draggable={false}
                          />
                          <span
                            className="truncate font-semibold tracking-wide text-white/95"
                            style={incomingPayoutLayout?.labelSpanStyle}
                          >
                            {t('pvp.directChallengeStakePlatformFeeTitle')}
                          </span>
                        </div>
                        <span
                          className="shrink-0 text-right font-bold tabular-nums text-[#c9a87a]"
                          style={incomingPayoutLayout?.valueStyle}
                        >
                          {INCOMING_PAYOUT_PLATFORM_MULT}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="grid min-h-0 min-w-0 flex-1"
                      style={{
                        gridTemplateRows: `repeat(${INCOMING_FREE_PAYOUT_BULLETS.length}, minmax(0, 1fr))`,
                        rowGap: '2%',
                        minHeight: 0,
                      }}
                    >
                      {INCOMING_FREE_PAYOUT_BULLETS.map((bullet) => (
                        <div
                          key={bullet.key}
                          className="flex min-h-0 items-center overflow-hidden px-[1cqi]"
                          style={{ containerType: 'size' }}
                        >
                          <span className="flex min-h-0 min-w-0 items-center gap-[2.5cqi]">
                            <img
                              src={INCOMING_UNRANKED_PAYOUT_ICON_URLS[bullet.iconIndex]}
                              alt=""
                              aria-hidden
                              className="shrink-0 object-contain"
                              draggable={false}
                              style={{
                                width: '35cqh',
                                height: '35cqh',
                                maxHeight: '100%',
                              }}
                            />
                            <span
                              className="min-w-0 flex-1 normal-case text-amber-50/95"
                              style={{
                                fontFamily:
                                  "'Segoe UI', system-ui, -apple-system, BlinkMacSystemFont, Roboto, sans-serif",
                                fontSize: '22cqh',
                                lineHeight: 1.25,
                              }}
                            >
                              {t(bullet.key)}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </div>

              <div className="mt-[2cqh] flex shrink-0 flex-row gap-[2cqi]">
                <button
                  type="button"
                  disabled={respondBusy}
                  onClick={handleDecline}
                  className="flex-1 rounded-lg border border-red-400/45 bg-red-950/60 py-[1.5cqh] text-sm font-medium text-red-50 hover:border-red-400/60 hover:bg-red-900/75 disabled:opacity-50"
                >
                  {t('pvp.directChallengeDecline')}
                </button>
                {incomingAcceptInsufficientFunds ? (
                  <span className="relative flex min-w-0 flex-1">
                    {acceptInsufficientBalanceTipOpen ? (
                      <span
                        role="tooltip"
                        className="absolute bottom-[calc(100%+0.5rem)] left-1/2 z-[4] w-max max-w-[min(18rem,80vw)] -translate-x-1/2 rounded-md border border-amber-400/35 bg-black/95 px-3 py-2 text-center text-xs leading-snug text-amber-100 shadow-[0_8px_24px_rgba(0,0,0,0.55)]"
                      >
                        {t('pvp.directChallengeAcceptInsufficientBalance')}
                      </span>
                    ) : null}
                    <button
                      type="button"
                      disabled
                      className="w-full flex-1 cursor-not-allowed rounded-lg border border-emerald-400/30 bg-emerald-950/55 py-[1.5cqh] text-sm font-medium text-emerald-200/45 opacity-70"
                    >
                      {t('pvp.directChallengeAccept')}
                    </button>
                    <span
                      role="presentation"
                      className="absolute inset-0 z-[1] cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setAcceptInsufficientBalanceTipOpen((o) => !o)
                      }}
                    />
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={respondBusy}
                    onClick={handleAccept}
                    className="flex-1 rounded-lg border border-emerald-400/50 bg-emerald-800/85 py-[1.5cqh] text-sm font-medium text-emerald-50 hover:border-emerald-400/65 hover:bg-emerald-700/90 disabled:opacity-50"
                  >
                    {t('pvp.directChallengeAccept')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      ) : null}

      {showAwaitingJoinModal && awaitingJoin ? (
        <div className="fixed inset-0 z-[84] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="direct-challenge-awaiting-join-title"
            className="flex min-h-0 min-w-0 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-amber-400/40 bg-gradient-to-b from-zinc-900/98 to-black/95 shadow-[0_16px_48px_rgba(0,0,0,0.65)]"
            style={{
              containerType: 'size',
              boxSizing: 'border-box',
              aspectRatio: '8 / 6',
              width: 'min(800px, 70vw, calc(min(600px, 70vh) * 8 / 6), calc(100vw - 2rem))',
              height: 'auto',
            }}
          >
            <div
              className="flex min-h-0 flex-1 flex-col overflow-hidden"
              style={{
                boxSizing: 'border-box',
                minHeight: 0,
                paddingLeft: '3cqi',
                paddingRight: '3cqi',
                paddingTop: '1cqh',
                paddingBottom: '3cqh',
              }}
            >
              <div
                className="flex w-full shrink-0 flex-row items-center justify-center gap-[0.35em]"
                style={{
                  flex: '0 0 12%',
                  minHeight: 0,
                  containerType: 'size',
                  width: '100%',
                }}
              >
                <img
                  src={PVP_LOBBY_PAGE_TITLE_DIVIDER_LEFT_URL}
                  alt=""
                  aria-hidden
                  className="min-h-0 min-w-0 max-w-[min(22%,5.5rem)] shrink object-contain"
                  style={{ height: '55cqh', maxHeight: '85cqh' }}
                  draggable={false}
                />
                <h2
                  id="direct-challenge-awaiting-join-title"
                  className="shrink-0 whitespace-nowrap text-center font-extrabold uppercase leading-none tracking-tight"
                  style={{
                    ...DIRECT_CHALLENGE_MODAL_TITLE_STYLE,
                    fontSize: '60cqh',
                    lineHeight: 1,
                  }}
                >
                  {t('pvp.directChallengePageTitle')}
                </h2>
                <img
                  src={PVP_LOBBY_PAGE_TITLE_DIVIDER_RIGHT_URL}
                  alt=""
                  aria-hidden
                  className="min-h-0 min-w-0 max-w-[min(22%,5.5rem)] shrink object-contain"
                  style={{ height: '55cqh', maxHeight: '85cqh' }}
                  draggable={false}
                />
              </div>

              <div
                className="w-full shrink-0"
                style={
                  directChallengeDeclinedMessage
                    ? {
                      /** Taller band vs waiting state so opponent avatar + info read larger. */
                      flex: '0 0 46%',
                      minHeight: 0,
                      width: '100%',
                      display: 'flex',
                      alignItems: 'stretch',
                      justifyContent: 'center',
                      containerType: 'size',
                    }
                    : {
                      flex: '0 0 28%',
                      minHeight: 0,
                      width: '100%',
                      display: 'grid',
                      gridTemplateColumns:
                        'minmax(0,30%) minmax(0,5%) minmax(0,30%) minmax(0,5%) minmax(0,30%)',
                      alignItems: 'stretch',
                      containerType: 'size',
                    }
                }
              >
                {directChallengeDeclinedMessage ? (
                  <div className="flex min-h-0 w-full max-w-full min-w-0 flex-1 items-center justify-center px-[1cqi]">
                    <AwaitingJoinPlayerStripColumn
                      layout="horizontal"
                      nickname={awaitingJoin.opponentNickname.trim()}
                      wallet={awaitingJoin.opponentWallet}
                      avatarUrl={(awaitingJoin.opponentAvatarUrl ?? '').trim() || null}
                      avatarFailed={awaitingJoinOpponentAvatarFailed}
                      onAvatarError={() => setAwaitingJoinOpponentAvatarFailed(true)}
                      level={
                        awaitingJoin.opponentUserLvl != null &&
                          Number.isFinite(Number(awaitingJoin.opponentUserLvl))
                          ? Math.max(1, Math.floor(Number(awaitingJoin.opponentUserLvl)))
                          : 1
                      }
                      mmr={
                        awaitingJoin.opponentPvpMmr != null &&
                          Number.isFinite(Number(awaitingJoin.opponentPvpMmr))
                          ? Math.floor(Number(awaitingJoin.opponentPvpMmr))
                          : 0
                      }
                    />
                  </div>
                ) : (
                  <>
                    <AwaitingJoinPlayerStripColumn
                      nickname={waitingModalSelfNickname}
                      wallet={address ?? ''}
                      avatarUrl={waitingModalSelfAvatarUrl}
                      avatarFailed={awaitingJoinSelfAvatarFailed}
                      onAvatarError={() => setAwaitingJoinSelfAvatarFailed(true)}
                      level={waitingModalSelfLevel}
                      mmr={waitingModalSelfMmr}
                    />
                    <div aria-hidden className="min-h-0 min-w-0" />
                    <div
                      aria-hidden
                      className="flex min-h-0 min-w-0 items-center justify-center"
                      style={{
                        height: '100%',
                        minHeight: 0,
                        containerType: 'size',
                      }}
                    >
                      <DirectChallengeAwaitingHourglassLottie
                        className="pointer-events-none h-[80cqh] w-[80cqh] max-h-full max-w-full shrink-0 select-none"
                        style={{
                          filter:
                            'drop-shadow(0 0 10px rgba(96, 165, 253, 0.55)) drop-shadow(0 0 22px rgba(37, 99, 235, 0.35))',
                        }}
                      />
                    </div>
                    <div aria-hidden className="min-h-0 min-w-0" />
                    <AwaitingJoinPlayerStripColumn
                      nickname={awaitingJoin.opponentNickname.trim()}
                      wallet={awaitingJoin.opponentWallet}
                      avatarUrl={(awaitingJoin.opponentAvatarUrl ?? '').trim() || null}
                      avatarFailed={awaitingJoinOpponentAvatarFailed}
                      onAvatarError={() => setAwaitingJoinOpponentAvatarFailed(true)}
                      level={
                        awaitingJoin.opponentUserLvl != null &&
                          Number.isFinite(Number(awaitingJoin.opponentUserLvl))
                          ? Math.max(1, Math.floor(Number(awaitingJoin.opponentUserLvl)))
                          : 1
                      }
                      mmr={
                        awaitingJoin.opponentPvpMmr != null &&
                          Number.isFinite(Number(awaitingJoin.opponentPvpMmr))
                          ? Math.floor(Number(awaitingJoin.opponentPvpMmr))
                          : 0
                      }
                    />
                  </>
                )}
              </div>

              <div
                className="min-h-0 w-full shrink"
                style={{
                  flex: '1 1 0%',
                  minHeight: 0,
                  containerType: 'size',
                  display: 'grid',
                  /** Declined: upper opponent band is 46% vs 28% when waiting — less space here; grow summary row % so its pixel height matches waiting (30% of larger grid ≈ 43% of smaller). */
                  gridTemplateRows: directChallengeDeclinedMessage
                    ? '29% 5% 43% 5% 18%'
                    : '42% 5% 30% 5% 18%',
                  boxSizing: 'border-box',
                }}
              >
                <div className="flex min-h-0 min-w-0 flex-col">
                  <div
                    className="flex min-h-0 w-full flex-1 flex-col items-center justify-center text-center"
                    style={{
                      containerType: 'size',
                      boxSizing: 'border-box',
                      height: '100%',
                      width: '100%',
                      minHeight: 0,
                      paddingLeft: '4cqi',
                      paddingRight: '4cqi',
                      paddingTop: '12cqh',
                      paddingBottom: '4cqh',
                      gap: '6cqh',
                    }}
                  >
                    <p
                      className="shrink-0 text-white"
                      style={{
                        fontFamily:
                          '"Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, "Roboto", sans-serif',
                        fontSize: directChallengeDeclinedMessage ? '70cqh' : '25cqh',
                        lineHeight: 1.25,
                        color: '#ffffff',
                        textShadow: directChallengeDeclinedMessage
                          ? '0 0 12px rgba(248, 113, 113, 0.95), 0 0 28px rgba(220, 38, 38, 0.65), 0 0 40px rgba(127, 29, 29, 0.45), 0 2px 8px rgba(0, 0, 0, 0.9)'
                          : '0 0 12px rgba(147, 197, 253, 0.95), 0 0 28px rgba(59, 130, 246, 0.7), 0 0 44px rgba(37, 99, 235, 0.45), 0 2px 8px rgba(0, 0, 0, 0.9)',
                      }}
                    >
                      {directChallengeDeclinedMessage
                        ? directChallengeDeclinedMessage
                        : awaitingJoin.countingDown
                          ? (dcCountdownEndsAt == null || dcCountdownDisplaySec <= 0
                              ? t('pvp.directChallengeBattleStarting')
                              : t('pvp.directChallengeCountdownTitle'))
                          : awaitingJoin.awaitingAcceptDecline
                            ? t('pvp.directChallengeWaitingAcceptOrDecline')
                            : t('pvp.directChallengeWaitingOpponentJoin')}
                    </p>
                    {!directChallengeDeclinedMessage ? (
                      awaitingJoin.countingDown ? (
                        dcCountdownEndsAt != null && dcCountdownDisplaySec > 0 ? (
                          <p
                            className="min-h-0 shrink-0 tabular-nums text-white"
                            style={{
                              fontFamily: "'Tilt Warp', sans-serif",
                              fontSize: '60cqh',
                              lineHeight: 1,
                            }}
                          >
                            {dcCountdownDisplaySec}
                          </p>
                        ) : null
                      ) : (
                        <>
                          <p
                            className="shrink-0 text-amber-200/90"
                            style={{
                              fontFamily:
                                '"Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, "Roboto", sans-serif',
                              fontSize: '20cqh',
                              lineHeight: 1.2,
                            }}
                          >
                            {t('pvp.directChallengeWaitingExpiresIn')}
                          </p>
                          <p
                            className="min-h-0 shrink-0 tabular-nums text-white"
                            style={{
                              fontFamily: "'Tilt Warp', sans-serif",
                              fontSize: '60cqh',
                              lineHeight: 1,
                            }}
                          >
                            {formatDirectChallengeJoinPrepMmSs(joinPrepRemainingMs / 1000)}
                          </p>
                        </>
                      )
                    ) : null}
                  </div>
                </div>
                <div aria-hidden className="min-h-0" />
                <div className="flex min-h-0 min-w-0 flex-col" style={{ minHeight: 0 }}>
                  {awaitingJoinSummaryColumns ? (
                    <DirectChallengeAwaitingJoinSummaryStrip columns={awaitingJoinSummaryColumns} />
                  ) : null}
                </div>
                <div aria-hidden className="min-h-0" />
                <div className="flex min-h-0 w-full min-w-0 items-center justify-center">
                  <button
                    type="button"
                    disabled={
                      cancelAwaitingJoinBusy ||
                      (!directChallengeDeclinedMessage && !awaitingJoin.awaitingAcceptDecline)
                    }
                    onClick={handleCancelAwaitingJoin}
                    className={
                      'inline-flex max-w-full w-max shrink-0 flex-nowrap items-center justify-center gap-[0.4em] whitespace-nowrap rounded-lg px-[1.5em] py-[0.72em] shadow-[0_6px_20px_rgba(0,0,0,0.45)] transition disabled:cursor-not-allowed disabled:opacity-55 ' +
                      (directChallengeDeclinedMessage
                        ? 'border border-emerald-400/55 bg-gradient-to-b from-emerald-950/95 to-emerald-900/90 text-emerald-50 enabled:hover:border-emerald-300/85 enabled:hover:from-emerald-900/95 enabled:hover:to-emerald-800/90'
                        : 'border border-red-500/75 bg-gradient-to-b from-red-950/95 to-red-900/90 text-red-50 enabled:hover:border-red-400/90 enabled:hover:from-red-900/95 enabled:hover:to-red-800/90')
                    }
                    style={{
                      height: '94%',
                      minHeight: 'clamp(50px, 7.2vmin, 68px)',
                      maxHeight: '100%',
                      boxSizing: 'border-box',
                      fontFamily: "'Tilt Warp', sans-serif",
                      fontSize: AWAITING_JOIN_FOOTER_BUTTON_FONT_SIZE,
                    }}
                  >
                    {directChallengeDeclinedMessage ? (
                      <span
                        className="shrink-0 leading-none"
                        style={{ fontSize: AWAITING_JOIN_FOOTER_BUTTON_FONT_SIZE, lineHeight: 1 }}
                      >
                        {t('pvp.directChallengeConfirm')}
                      </span>
                    ) : (
                      <>
                        <CloseCanvasIcon
                          className="shrink-0"
                          style={{
                            fontSize:
                              'clamp(14px, min(5.64cqh, 5.6vmin), 22px)',
                            width: '1em',
                            height: '1em',
                          }}
                        />
                        <span className="shrink-0 leading-none">
                          {t('pvp.directChallengeCancelChallenge')}
                        </span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {dcExpiredModal ? (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="direct-challenge-expired-title"
            className="flex max-h-[min(90vh,560px)] w-full max-w-md flex-col overflow-hidden rounded-xl border border-amber-400/40 bg-gradient-to-b from-zinc-900/98 to-black/95 px-6 py-8 shadow-[0_16px_48px_rgba(0,0,0,0.65)]"
          >
            <h2
              id="direct-challenge-expired-title"
              className="mb-4 shrink-0 text-center font-extrabold uppercase tracking-wide"
              style={{
                ...DIRECT_CHALLENGE_MODAL_TITLE_STYLE,
                fontSize: 'clamp(1.15rem, 4.2vmin, 1.65rem)',
                lineHeight: 1.15,
              }}
            >
              {t('pvp.directChallengeExpiredTitle')}
            </h2>
            <p
              className="mb-8 shrink text-center text-white/95"
              style={{
                fontFamily:
                  '"Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, "Roboto", sans-serif',
                fontSize: 'clamp(0.95rem, 3.2vmin, 1.05rem)',
                lineHeight: 1.45,
              }}
            >
              {dcExpiredModal.phase === 'pending'
                ? t('pvp.directChallengePendingExpired')
                : t('pvp.directChallengeJoinPrepExpired')}
            </p>
            <button
              type="button"
              onClick={handleDismissDirectChallengeExpiredModal}
              className="mx-auto inline-flex max-w-full shrink-0 items-center justify-center rounded-lg border border-amber-400/50 bg-amber-950/90 px-6 py-2.5 text-amber-50 shadow-[0_6px_20px_rgba(0,0,0,0.45)] transition hover:border-amber-300/80 hover:bg-amber-900/95"
              style={{
                fontFamily: "'Tilt Warp', sans-serif",
                fontSize: 'clamp(13px, 3.4vmin, 15px)',
              }}
            >
              {dcExpiredModal.deferredClear
                ? t('pvp.directChallengeExpiredReturnToLobby')
                : t('pvp.directChallengeConfirm')}
            </button>
          </div>
        </div>
      ) : null}

      {dcCountdownEndsAt != null ? (
        <div className="pointer-events-none fixed inset-0 z-[83] flex items-center justify-center bg-black/40">
          <div className="rounded-2xl border border-amber-400/45 bg-black/85 px-10 py-6 text-center shadow-xl">
            <p className="text-sm uppercase tracking-wide text-amber-200/90">{t('pvp.directChallengeCountdownTitle')}</p>
            <p className="mt-2 text-5xl font-bold tabular-nums text-white" style={{ fontFamily: "'Tilt Warp', sans-serif" }}>
              {dcCountdownDisplaySec > 0 ? dcCountdownDisplaySec : t('pvp.directChallengeBattleStarting')}
            </p>
          </div>
        </div>
      ) : null}
    </>
  )
}
